// src/components/ToggleThemeButton/ToggleThemeButton.tsx
import React from 'react';
import styled from 'styled-components';
import type { ComponentType, SVGProps } from 'react';
import { FiMoon, FiSun } from 'react-icons/fi';

import { MEDIA_BREAKPOINTS } from '@/constants/layout';
import { useTheme } from '@/theme/ThemeProvider';

import { Icon } from '../StyledComponents';

const StyledToggleThemeButton = styled.button<{ $compactDesktop?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;

  background-color: ${({ theme }) => theme.menuColor};
  color: ${({ theme }) => theme.textColor};

  padding: 2px 6px;
  cursor: pointer;
  appearance: none;

  border: 1px solid ${({ theme }) => theme.textColor};
  border-radius: 14px;

  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease,
    background-color 0.25s ease;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.MenuItemSelectedColor};
    outline-offset: 3px;
  }

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    &:hover {
      transform: scale(1.08);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    }

    &:hover > div:first-child svg {
      transform: rotate(18deg) scale(1.08);
    }

    &:hover > div:last-child svg {
      transform: translateY(-1px) rotate(-12deg) scale(1.08);
    }
  }

  ${({ $compactDesktop }) =>
    $compactDesktop
      ? `
      @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) and (max-width: 1120px) {
        gap: 2px;
        padding: 1px 4px;
        border-radius: 12px;
      }
    `
      : ''}
`;

const ToggleIcon = styled.div<{
  $isVisible: boolean;
  $compactDesktop?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.sizes.small.iconWidth};
  height: ${({ theme }) => theme.sizes.small.iconHeight};
  pointer-events: none;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transform: ${({ $isVisible }) => ($isVisible ? 'scale(1)' : 'scale(0.72)')};
  transition:
    color 0.3s ease,
    opacity 0.22s ease,
    transform 0.22s ease;
  color: ${({ theme }) => theme.themeSelectColor};

  svg {
    transition: transform 0.25s ease;
    transform-origin: center;
  }

  ${({ $compactDesktop }) =>
    $compactDesktop
      ? `
      @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) and (max-width: 1120px) {
        width: 20px;
        height: 20px;

        svg {
          width: 16px;
          height: 16px;
        }
      }
    `
      : ''}
`;

const SunIcon = styled(FiSun as ComponentType<SVGProps<SVGSVGElement>>)``;

const MoonIcon = styled(FiMoon as ComponentType<SVGProps<SVGSVGElement>>)``;

const ToggleThemeButton: React.FC<{
  onToggle?: () => void;
  compactDesktop?: boolean;
}> = ({ onToggle, compactDesktop = false }) => {
  const { toggleTheme, mode } = useTheme();
  const isDarkMode = mode === 'dark';

  const handleClick = () => {
    toggleTheme();
    onToggle?.();
  };

  return (
    <StyledToggleThemeButton
      type="button"
      onClick={handleClick}
      $compactDesktop={compactDesktop}
      aria-label={isDarkMode ? 'עבור למצב בהיר' : 'עבור למצב כהה'}
      aria-pressed={!isDarkMode}
    >
      <Icon>
        <ToggleIcon $isVisible={!isDarkMode} $compactDesktop={compactDesktop}>
          <SunIcon aria-hidden="true" focusable="false" />
        </ToggleIcon>
      </Icon>
      <Icon>
        <ToggleIcon $isVisible={isDarkMode} $compactDesktop={compactDesktop}>
          <MoonIcon aria-hidden="true" focusable="false" />
        </ToggleIcon>
      </Icon>
    </StyledToggleThemeButton>
  );
};

export default ToggleThemeButton;
